/**
 * The badge that opens every `Widget` header — a tinted circle around a small
 * stroke icon. Five shapes, each named for what the section is about rather than
 * how it looks, so a call site reads `icon="ledger"` instead of guessing between
 * two similar glyphs.
 *
 * `tone` is a category color, not a status color. Late/current still comes from
 * `.pill`/`.pill--late` next to the actual number — this only tints which shelf a
 * widget sits on (money, access, operations), so it stays legible if a reader
 * can't see color at all.
 */
export type WidgetIconName = 'ledger' | 'shield' | 'clock' | 'info' | 'wrench' | 'chart';
export type WidgetTone = 'green' | 'blue' | 'orange';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Glyph({ name }: { name: WidgetIconName }) {
  switch (name) {
    case 'ledger':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <path d="M4 6h16M4 12h11M4 18h14" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <path d="M12 3l7 3v5c0 5-3.2 8.4-7 10-3.8-1.6-7-5-7-10V6l7-3z" />
        </svg>
      );
    case 'clock':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.5V12l3 2.2" />
        </svg>
      );
    case 'info':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 10.5v5.2M12 8v.01" />
        </svg>
      );
    case 'wrench':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <path d="M14.7 6.3a4 4 0 10-5 5L4 17v3h3l5.7-5.7a4 4 0 005-5l-2.8 2.8-2.3-2.3 2.8-2.8z" />
        </svg>
      );
    case 'chart':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" {...STROKE}>
          <path d="M4 20V4M4 20h16M8 16v-5M12.5 16V8M17 16v-8" />
        </svg>
      );
  }
}

export function WidgetIcon({ name, tone = 'green' }: { name: WidgetIconName; tone?: WidgetTone }) {
  return (
    <span className={`widget__icon widget__icon--${tone}`} aria-hidden="true">
      <Glyph name={name} />
    </span>
  );
}
